import React, { useId } from "react";
import { Badge, Btn, InsightBox, Overline, Select, TextArea } from "../../../ForBrands/ui/BrandUI";
import QtyStepper from "../../../ForBrands/ui/QtyStepper";
import { ContextStrip, FormFieldM, KeyValueRow, MobileCard, MobilePageHead } from "../../../ForBrands/ui/mobile/MobileUI";
import { DealScreen, DiffRow } from "./OfferMobile";

// Athlete Mode · counter offer form on phones (Figma 2000:967 "05 · Counter
// Offer Form"). The page keeps the form state, the live summary and
// analysis, and send(); this is only the phone tree. Decision D-a17: two
// native date inputs in a two-column grid, the numeric exclusivity field
// with its "days" suffix, and boxed steppers for the deliverable counts.
// Every field sits in `bm-phone-form` (56px controls, 16px text). The date
// inputs take `bm-input-lg` directly: its element-qualified rule outranks the
// forms plugin's [type='date'] border, which `.bm-input` does not.

// The 56px control around an input with a prefix or suffix ("$", "days").
const BOX =
  "flex h-14 items-center gap-1 rounded-[12px] border border-bm-border bg-white px-[18px] focus-within:border-bm-ink focus-within:ring-1 focus-within:ring-inset focus-within:ring-bm-ink";
const BARE = "bm-bare min-w-0 flex-1 bg-transparent font-inter text-[16px] leading-[22px] text-bm-ink outline-none placeholder:text-bm-placeholder";
const AFFIX = "font-inter text-[16px] leading-[22px] text-bm-muted";

// Summary chips keep the web tones (outline with the green dot, dark, passive).
const summaryBadge = (meta) => (
  <Badge
    tone={meta.tone === "green" ? "outline" : meta.tone === "dark" ? "dark" : "passive"}
    dot={meta.tone === "green" ? "#1f9d55" : undefined}
  >
    {meta.chip}
  </Badge>
);

const CardHead = ({ overline, title, id }) => (
  <div className="flex flex-col gap-[2px]">
    <Overline>{overline}</Overline>
    <h2 id={id} className="bm-h-s text-bm-ink">
      {title}
    </h2>
  </div>
);

const CounterMobile = ({
  onBack,
  onCancel,
  campaign,
  brand,
  range,
  reference,
  form,
  update,
  updateRow,
  schedules,
  scheduleHint,
  summaryRows,
  analysis,
  busy,
  onSend,
}) => {
  const uid = useId();
  const ids = {
    compensation: `${uid}-compensation`,
    deliverables: `${uid}-deliverables`,
    timeline: `${uid}-timeline`,
    schedule: `${uid}-schedule`,
    exclusivity: `${uid}-exclusivity`,
    note: `${uid}-note`,
  };
  const rowLabel = (row) => `${row.platform} ${row.contentType}${row.contentType?.endsWith("s") ? "" : "s"}`;

  return (
    <DealScreen
      title="Counter Proposal"
      onBack={onBack}
      backLabel="Back to offer"
      strip={<ContextStrip title={campaign?.name} badge={<Badge tone="outline">Counter proposal</Badge>} meta={`${brand.company} · ${range}`} />}
      actions={
        <>
          <Btn size="lg" className="w-full" onClick={onSend} disabled={busy}>
            {busy ? "Sending..." : "Send counter"}
          </Btn>
          <Btn size="lg" variant="ghost" className="w-full" onClick={onCancel}>
            Cancel
          </Btn>
        </>
      }
    >
      <MobilePageHead
        eyebrow="Deal negotiation"
        eyebrowBrand
        title="Propose Counter Terms"
        sub="Propose your terms below. The brand will review and can accept, counter back, or decline."
      />

      <MobileCard>
        <CardHead overline="Reference" title="Original Brand Offer" />
        <div>
          {reference.map(([label, value], i) => (
            <KeyValueRow key={label} label={label} value={value} last={i === reference.length - 1} />
          ))}
        </div>
      </MobileCard>

      <section aria-labelledby={`${uid}-form`} className="bm-phone-form flex flex-col gap-4">
        <CardHead overline="Negotiation form" title="Counter Proposal" id={`${uid}-form`} />

        <FormFieldM label="Proposed compensation" htmlFor={ids.compensation} hint="Enter your proposed total compensation. Cache fee applies to the brand.">
          <span className={BOX}>
            <span aria-hidden="true" className={AFFIX}>
              $
            </span>
            <input
              id={ids.compensation}
              type="number"
              min="0"
              inputMode="decimal"
              aria-describedby={`${ids.compensation}-hint`}
              value={form.compensation}
              onChange={(e) => update({ compensation: e.target.value })}
              className={BARE}
            />
          </span>
        </FormFieldM>

        <MobileCard role="group" aria-labelledby={ids.deliverables}>
          <span id={ids.deliverables} className="bm-label-l text-bm-ink">
            Proposed deliverables
          </span>
          {form.deliverables.map((row, idx) => (
            <div key={`${row.platform}-${row.contentType}-${idx}`} className="flex items-center gap-3">
              <span className="min-w-0 flex-1 break-words bm-body-m text-bm-muted">{rowLabel(row)}</span>
              <QtyStepper variant="boxed" label={rowLabel(row)} value={row.quantity} min={0} onChange={(v) => updateRow(idx, v)} />
            </div>
          ))}
          <p className="bm-body-xs text-bm-muted">Adjust deliverable quantities. Adding deliverables may support a higher compensation ask.</p>
        </MobileCard>

        <FormFieldM label={<span id={ids.timeline}>Proposed timeline</span>} hint="Suggest a different campaign window if needed.">
          <div role="group" aria-labelledby={ids.timeline} className="grid grid-cols-2 gap-3">
            <label className="flex min-w-0 flex-col gap-1">
              <span className="bm-body-xs text-bm-muted">Start</span>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => update({ startDate: e.target.value })}
                className="bm-input-lg min-w-0 !px-[14px]"
              />
            </label>
            <label className="flex min-w-0 flex-col gap-1">
              <span className="bm-body-xs text-bm-muted">End</span>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => update({ endDate: e.target.value })}
                className="bm-input-lg min-w-0 !px-[14px]"
              />
            </label>
          </div>
        </FormFieldM>

        <FormFieldM label="Proposed payment schedule" htmlFor={ids.schedule} hint={scheduleHint}>
          <Select id={ids.schedule} aria-describedby={`${ids.schedule}-hint`} value={form.schedule} onChange={(e) => update({ schedule: e.target.value })}>
            {schedules.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </FormFieldM>

        <FormFieldM label="Proposed exclusivity" htmlFor={ids.exclusivity} hint="Shorter exclusivity windows give you more flexibility with other brands.">
          <span className={BOX}>
            <input
              id={ids.exclusivity}
              type="number"
              min="0"
              max="365"
              inputMode="numeric"
              aria-describedby={`${ids.exclusivity}-hint`}
              value={form.exclusivityDays}
              onChange={(e) => update({ exclusivityDays: e.target.value })}
              className={BARE}
            />
            <span className={AFFIX}>days</span>
          </span>
        </FormFieldM>

        <FormFieldM label="Message to brand (optional)" htmlFor={ids.note}>
          <TextArea
            id={ids.note}
            rows={4}
            value={form.note}
            onChange={(e) => update({ note: e.target.value })}
            placeholder={`Hi! Excited about ${campaign?.name || "this campaign"}. Here's why I'm proposing these terms...`}
          />
        </FormFieldM>
      </section>

      <MobileCard>
        <CardHead overline="Summary of proposal" title="Counter Proposal Preview" />
        {summaryRows.map(([label, value, meta]) => (
          <DiffRow key={label} label={label} value={value} badge={summaryBadge(meta)} />
        ))}
      </MobileCard>

      <InsightBox compact title="Cache AI counter analysis">
        {analysis}
      </InsightBox>
    </DealScreen>
  );
};

export default CounterMobile;
